import React, { useState, useEffect, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import MainHeader from './MainHeader';
import MainLoader from '../Structures/MainLoader';
import SimpleFooter from '../Structures/SimpleFooter';
import { useLocation, Outlet, useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  getUser,
  setState,
  reset as userReset,
  setRedirectUrl,
  fetchUpdatedTracks,
  setFooter,
} from '../../features/user/userSlice';
import { reset as billReset } from '../../features/bills/billSlice';
import stateConfig from '../../stateConfig';
import '../../styles/Dashboard.css';

const changeFavicon = (iconURL) => {
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.getElementsByTagName('head')[0].appendChild(link);
  }
  link.href = iconURL;
};

const Layout = () => {
  const { searchTerm, state } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const user = useSelector((state) => state.user);

  const [isLoading, setIsLoading] = useState(false);
  const [canAccess, setCanAccess] = useState(user.auth ? true : false);
  const [footerLanguage, setFooterLanguage] = useState('');

  const prefix = state.toLowerCase();
  const config = stateConfig[prefix];

  const prevSessionRef = useRef();

  useEffect(() => {
    if (canAccess && user.searchTerm && searchTerm) {
      const newPath = location.pathname.replace(`/${searchTerm}`, '');
      navigate(`${newPath}${location.search || ''}`, { replace: true });
    }

    if (prevSessionRef.current && prevSessionRef.current !== user.session && user.session)
      dispatch(fetchUpdatedTracks(user.searchTerm));

    prevSessionRef.current = user.session;
  }, [canAccess, user.session]);

  useEffect(() => {
    changeFavicon(config.icon);
    document.title = config.tabTitle;
    dispatch(setRedirectUrl(config.website));
    dispatch(setFooter(config.footer));
    setFooterLanguage(config.footer);

    const handleMessage = (event) => {
      if (event.origin === config.website) {
        if (event.data === 'clearLocalStorage') {
          localStorage.clear();
          // Storage cleared via cross-origin message
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [user.state]);

  useEffect(() => {
    const checkTokenExpiration = () => {
      const user = JSON.parse(localStorage.getItem('user'));

      if (!user || !user.token) return;

      try {
        const decodedToken = jwtDecode(user.token);
        const currentTime = Date.now() / 1000;

        if (decodedToken.exp < currentTime) {
          console.error('Token has expired');
          if (user.auth !== 'login') {
            resetAuth();
          } else {
            navigate('/');
          }
        }
      } catch (error) {
        console.error('Error decoding token:', error);
        localStorage.removeItem('user');
      }
    };

    checkTokenExpiration();
  }, []);

  useEffect(() => {
    runAuth();
  }, [user.auth]);

  const runAuth = async () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const basePath = pathSegments[1]?.toLowerCase();

    const isBillPage = basePath === 'bills';

    const isLobbyRelatedPage =
      basePath === 'lobby' ||
      basePath === 'lobbyist' ||
      basePath === 'company' ||
      basePath === 'contactme' ||
      basePath === 'taskmaster';

    if (isLobbyRelatedPage || location.pathname.includes('/videos/share')) {
      setCanAccess(true);
      if (!user.auth) dispatch(setState(state.toUpperCase()));
      return;
    } else if (!user?.auth && searchTerm && !isLoading && (isBillPage || !isLobbyRelatedPage)) {
      setIsLoading(true);
      try {
        const data = await dispatch(getUser(searchTerm)).unwrap();
        if (!data?.auth) {
          console.log('UNAUTHENTICATED ', data?.auth);
          window.location.href = config.website;
        }
        setIsLoading(false);
        setCanAccess(true);
      } catch (error) {
        console.error(error);
        window.location.href = config.website;
      }
    } else if (!user?.auth && !searchTerm && !isLoading && !isLobbyRelatedPage) {
      window.location.href = config.website;
    }

    if (user.state && state.toUpperCase() !== user.state.toUpperCase() && !isLobbyRelatedPage) {
      if (searchTerm?.includes(state.toUpperCase())) {
        resetAuth();
      } else {
        setCanAccess(false);
      }
    }
  };

  const resetAuth = () => {
    localStorage.clear();
    dispatch(userReset());
    dispatch(billReset());
  };

  return (
    <>
      <div className="flex flex-col min-h-[calc(100vh)] bg-slate-50">
        <MainHeader />
        {canAccess && (
          <div className="flex-grow w-full pt-[75px] pb-10 relative print:m-0 print:p-0 print:pt-0">
            <Outlet />
          </div>
        )}
        {isLoading && (
          <div className="h-screen flex justify-center items-center">
            <MainLoader />
          </div>
        )}
        <SimpleFooter footerLanguage={footerLanguage} />
      </div>
    </>
  );
};

export default Layout;
